import React from "react";
import {
  AbsoluteFill,
  Audio,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { theme } from "./theme";
import {
  AtmosphericParticles,
  DynamicSubtitle,
  FilmGrain,
  SpecularShine,
} from "./components";

export const KyuStoreFlagshipMaster: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();

  // Helper spring shortcut (Remotion Pro pattern)
  const sp = (delay: number, damping = 200, stiffness = 120) =>
    spring({
      frame,
      fps,
      delay,
      config: { damping, stiffness },
    });

  // Dynamic Audio Ducking: ducks BGM while Orus speaks (frames 0 to 950)
  const bgmVolume = (f: number) => {
    const fadeIn = interpolate(f, [0, 25], [0, 1], { extrapolateRight: "clamp" });
    const fadeOut = interpolate(f, [durationInFrames - 30, durationInFrames], [1, 0], {
      extrapolateLeft: "clamp",
    });
    const duck = f < 945 ? 0.12 : 0.45;
    return duck * Math.min(fadeIn, fadeOut);
  };

  // Subtitles pinned strictly at safe band bottom: 400px (Y: 1520px)
  const SUBTITLES = [
    { from: 0, to: 65, text: "Bukan sulap, bukan trik.", highlight: "Bukan sulap, bukan trik" },
    { from: 65, to: 228, text: "Di Kyu Store, diamond Mobile Legends masuk ke akunmu dalam nol koma delapan detik!", highlight: "dalam nol koma delapan detik!" },
    { from: 228, to: 462, text: "Seratus persen resmi Moonton Direct API, anti-minus bergaransi, dan cukup masukkan User ID tanpa perlu password.", highlight: "resmi Moonton Direct API" },
    { from: 462, to: 715, text: "Dapatkan Weekly Diamond Pass promo flash sale paling hemat, lengkap dengan fitur notifikasi otomatis biar kamu gak ketinggalan event skin murah.", highlight: "Weekly Diamond Pass promo flash sale" },
    { from: 715, to: 845, text: "Selesai scan QRIS, pesananmu dipantau live detik demi detik.", highlight: "pesananmu dipantau live detik demi detik" },
    { from: 845, to: 1010, text: "Mau top up tenang dan satset? Buka kyustore.com sekarang juga!", highlight: "Buka kyustore.com sekarang juga!" },
  ];

  // 1. Continuous Organic Camera Drift (3d-and-cinematic.md §3)
  const driftY = Math.sin(frame / 32) * 2.8;
  const driftX = Math.cos(frame / 38) * 1.8;

  // Scene camera rotation transitions
  const camSceneRotY = interpolate(
    frame,
    [0, 65, 228, 462, 715, 845, 1010],
    [-4, 5, -5, 5, -4, 4, 0],
    { easing: theme.ease }
  );
  const camSceneRotX = interpolate(
    frame,
    [0, 65, 228, 462, 715, 845, 1010],
    [5, 2, 4, 3, 2, 3, 0],
    { easing: theme.ease }
  );

  const totalRotY = camSceneRotY + driftY;
  const totalRotX = camSceneRotX + driftX;

  // 2. Impact Frame Shake & Zoom Punch on the 0.82s hit (Frames 148 - 156)
  const isImpact = frame >= 148 && frame <= 156;
  const impactShakeX = isImpact ? (frame % 2 === 0 ? 10 : -10) : 0;
  const impactShakeY = isImpact ? (frame % 3 === 0 ? -6 : 6) : 0;
  const zoomPunch = interpolate(frame, [146, 149, 162], [1, 1.07, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: theme.ease,
  });

  // 3. Anticipation Dip on Button / Cards (Act 5)
  const ctaSpring = sp(855, 200);
  const ctaDip = interpolate(frame, [850, 855, 866], [0, 8, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const ctaPulse = Math.sin((frame - 885) * 0.18) * 0.03 + 1.0;

  // 4. Bell Ringing Shockwave (Act 3)
  const bellRing = Math.sin((frame - 470) * 0.6) * Math.exp(-((frame - 470) % 60) * 0.05) * 14;

  // 5. Specular Shine
  const shineProgress = interpolate(frame % 200, [0, 55], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: theme.ease,
  });

  return (
    <AbsoluteFill
      style={{
        backgroundColor: theme.colors.bg,
        fontFamily: theme.fonts.headline,
        color: theme.colors.text,
        overflow: "hidden",
      }}
    >
      {/* Procedural Film Grain Overlay (4.5% SVG feTurbulence) */}
      <FilmGrain opacity={0.045} />

      {/* Audio Engine with Pro Dynamic Ducking */}
      <Audio src={staticFile("kyustore_flagship_mastered.mp3")} volume={1.0} />
      <Audio src={staticFile("bgm-clean.mp3")} volume={bgmVolume} />

      {/* Layered SFX Cues Synced per Frame */}
      {frame === 4 && <Audio src={staticFile("whoosh-short.mp3")} volume={0.65} />}
      {frame === 70 && <Audio src={staticFile("swoosh-quick.mp3")} volume={0.7} />}
      {frame === 148 && <Audio src={staticFile("ui-success.mp3")} volume={0.9} />}
      {frame === 149 && <Audio src={staticFile("chime.mp3")} volume={0.85} />}

      {frame === 230 && <Audio src={staticFile("whoosh.mp3")} volume={0.7} />}
      {frame === 270 && <Audio src={staticFile("pop.mp3")} volume={0.7} />}
      {frame === 320 && <Audio src={staticFile("chime.mp3")} volume={0.75} />}

      {frame === 465 && <Audio src={staticFile("whoosh-short.mp3")} volume={0.7} />}
      {frame === 475 && <Audio src={staticFile("chime.mp3")} volume={0.8} />}

      {frame === 720 && <Audio src={staticFile("swoosh-quick.mp3")} volume={0.75} />}
      {frame === 750 && <Audio src={staticFile("pop.mp3")} volume={0.7} />}
      {frame === 780 && <Audio src={staticFile("ui-success.mp3")} volume={0.8} />}

      {frame === 850 && <Audio src={staticFile("whoosh.mp3")} volume={0.75} />}
      {frame === 870 && <Audio src={staticFile("click.mp3")} volume={0.8} />}

      {/* Background Subtle Tech Grid */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `
            radial-gradient(rgba(93, 63, 211, 0.08) 1.5px, transparent 1.5px),
            linear-gradient(to right, rgba(93, 63, 211, 0.035) 1px, transparent 1px)
          `,
          backgroundSize: "32px 32px, 64px 64px",
          pointerEvents: "none",
        }}
      />

      {/* Ambient Radial Spotlight */}
      <div
        style={{
          position: "absolute",
          top: "45%",
          left: "50%",
          width: 900,
          height: 900,
          transform: "translate(-50%, -50%)",
          background: "radial-gradient(circle, rgba(93, 63, 211, 0.14) 0%, rgba(248, 246, 254, 0) 70%)",
          filter: "blur(90px)",
          pointerEvents: "none",
        }}
      />

      {/* Top Header HUD Bar */}
      <div
        style={{
          position: "absolute",
          top: 80,
          left: theme.spacing.page,
          right: theme.spacing.page,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          zIndex: 50,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <Img src={staticFile("kyu_logo.png")} style={{ height: 48, objectFit: "contain" }} />
          <div style={{ width: 2, height: 26, backgroundColor: "#CBD5E1" }} />
          <span
            style={{
              fontFamily: theme.fonts.technical,
              fontSize: 18,
              fontWeight: 700,
              letterSpacing: "0.12em",
              color: theme.colors.muted,
            }}
          >
            [ OFFICIAL FLAGSHIP SHOWCASE ]
          </span>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            backgroundColor: theme.colors.surface,
            border: `2px solid ${theme.colors.success}`,
            padding: "8px 22px",
            borderRadius: 24,
            boxShadow: "0 4px 15px rgba(16, 185, 129, 0.15)",
          }}
        >
          <div style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: theme.colors.success, boxShadow: "0 0 10px #10B981" }} />
          <span
            style={{
              fontFamily: theme.fonts.technical,
              fontSize: 18,
              fontWeight: 700,
              color: theme.colors.success,
              letterSpacing: "0.08em",
            }}
          >
            MOONTON DIRECT API
          </span>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* 3D PERSPECTIVE VIEWPORT (1400px) with IMPACT SHAKE & ZOOM PUNCH           */}
      {/* ========================================================================= */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          perspective: 1400,
          perspectiveOrigin: "50% 48%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transform: `translate(${impactShakeX}px, ${impactShakeY}px) scale(${zoomPunch})`,
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            position: "relative",
            width: 900,
            height: 1100,
            transformStyle: "preserve-3d",
            transform: `rotateY(${totalRotY}deg) rotateX(${totalRotX}deg)`,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >

          {/* ===================================================================== */}
          {/* ACT 1: HOOK & SCALE CONTRAST (Frames 0 - 228)                         */}
          {/* Colossal 190px Bebas Neue Tabular-Nums `0.82 DETIK`                    */}
          {/* ===================================================================== */}
          {frame < 232 && (
            <div
              style={{
                position: "absolute",
                transformStyle: "preserve-3d",
                opacity: interpolate(frame, [212, 230], [1, 0], { extrapolateLeft: "clamp" }),
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                zIndex: 20,
              }}
            >
              <div
                style={{
                  backgroundColor: theme.colors.accentLight,
                  border: `2px solid ${theme.colors.accent}`,
                  borderRadius: 24,
                  padding: "8px 28px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 16,
                  boxShadow: "0 6px 20px rgba(93, 63, 211, 0.18)",
                  transform: `scale(${interpolate(sp(4, 200), [0, 1], [0.8, 1])})`,
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={theme.colors.accent} strokeWidth="3">
                  <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
                </svg>
                <span style={{ fontFamily: theme.fonts.technical, fontSize: 18, fontWeight: 800, color: theme.colors.accent, letterSpacing: "0.08em" }}>
                  BUKAN SULAP • BUKAN TRIK
                </span>
              </div>

              <h1
                style={{
                  fontFamily: theme.fonts.display,
                  fontSize: 72,
                  textAlign: "center",
                  lineHeight: 1.0,
                  marginBottom: 20,
                  transform: `scale(${interpolate(sp(12, 200), [0, 1], [0.85, 1])})`,
                }}
              >
                DIAMOND MASUK KE AKUNMU <br />
                <span style={{ color: theme.colors.accent }}>HANYA DALAM HITUNGAN DETIK</span>
              </h1>

              {/* Master 3D Stage with Scale Contrast Hero */}
              <div
                style={{
                  position: "relative",
                  width: 840,
                  height: 520,
                  backgroundColor: theme.colors.surfaceSubtle,
                  backdropFilter: "blur(20px)",
                  borderRadius: 38,
                  border: `3px solid ${theme.colors.accent}`,
                  boxShadow: "0 35px 85px rgba(93, 63, 211, 0.22), 0 10px 25px rgba(0,0,0,0.05)",
                  padding: "36px 40px",
                  transformStyle: "preserve-3d",
                  transform: `scale(${interpolate(sp(20, 200), [0, 1], [0.88, 1])})`,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <SpecularShine progress={shineProgress} angle={28} coreWidth={90} haloWidth={260} borderRadius={38} />

                <div
                  style={{
                    fontFamily: theme.fonts.technical,
                    fontSize: 20,
                    fontWeight: 700,
                    color: theme.colors.muted,
                    letterSpacing: "0.15em",
                    marginBottom: 4,
                    transform: "translateZ(30px)",
                  }}
                >
                  DURASI PROSES INJECT MOONTON REALTIME
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: 16,
                    transform: "translateZ(65px)",
                    margin: "8px 0",
                  }}
                >
                  <span
                    style={{
                      fontFamily: theme.fonts.display,
                      fontSize: 190,
                      lineHeight: 0.85,
                      color: theme.colors.success,
                      fontVariantNumeric: "tabular-nums",
                      textShadow: "0 15px 45px rgba(16, 185, 129, 0.35)",
                      letterSpacing: "0.02em",
                    }}
                  >
                    0.82
                  </span>
                  <span
                    style={{
                      fontFamily: theme.fonts.display,
                      fontSize: 80,
                      color: theme.colors.text,
                      letterSpacing: "0.04em",
                    }}
                  >
                    DETIK
                  </span>
                </div>

                <div
                  style={{
                    width: "100%",
                    backgroundColor: theme.colors.darkBadge,
                    color: "#FFFFFF",
                    borderRadius: 22,
                    padding: "16px 28px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    transform: "translateZ(45px)",
                    marginTop: 14,
                    boxShadow: "0 15px 35px rgba(30, 18, 74, 0.35)",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                    <Img src={staticFile("diamond_3d_crystal.png")} style={{ width: 48, height: 48, objectFit: "contain" }} />
                    <span style={{ fontFamily: theme.fonts.display, fontSize: 32, letterSpacing: "0.02em" }}>
                      HASIL TRANSAKSI TERKONFIRMASI DI GAME
                    </span>
                  </div>
                  <div style={{ fontFamily: theme.fonts.technical, fontSize: 16, fontWeight: 800, color: theme.colors.success }}>
                    100% SUKSES
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ===================================================================== */}
          {/* ACT 2: AUTHENTIC IN-GAME MLBB GAMEPLAY SHOWCASE (Frames 228 - 462)     */}
          {/* Authentic Gameplay Screenshot: Gusion Skill 2 on Sanctum Island        */}
          {/* ===================================================================== */}
          {frame >= 222 && frame < 468 && (
            <div
              style={{
                position: "absolute",
                transformStyle: "preserve-3d",
                opacity: interpolate(
                  frame,
                  [222, 238, 448, 465],
                  [0, 1, 1, 0],
                  { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
                ),
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                zIndex: 20,
              }}
            >
              <div
                style={{
                  backgroundColor: theme.colors.successLight,
                  border: `2px solid ${theme.colors.success}`,
                  padding: "6px 24px",
                  borderRadius: 22,
                  marginBottom: 14,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  boxShadow: "0 4px 15px rgba(16, 185, 129, 0.15)",
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={theme.colors.success} strokeWidth="3">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"/>
                </svg>
                <span style={{ fontFamily: theme.fonts.technical, fontSize: 18, fontWeight: 700, color: theme.colors.success, letterSpacing: "0.08em" }}>
                  100% RESMI MOONTON DIRECT API
                </span>
              </div>

              <h1
                style={{
                  fontFamily: theme.fonts.display,
                  fontSize: 72,
                  textAlign: "center",
                  lineHeight: 1.0,
                  marginBottom: 20,
                }}
              >
                ANTI-MINUS BERGARANSI, <span style={{ color: theme.colors.accent }}>TANPA PERLU PASSWORD</span>
              </h1>

              {/* Master 3D In-Game Showcase Stage */}
              <div
                style={{
                  position: "relative",
                  width: 860,
                  height: 540,
                  backgroundColor: theme.colors.surfaceSubtle,
                  backdropFilter: "blur(20px)",
                  borderRadius: 38,
                  border: `3px solid ${theme.colors.accent}`,
                  boxShadow: "0 35px 85px rgba(93, 63, 211, 0.22), 0 10px 25px rgba(0,0,0,0.05)",
                  padding: "18px",
                  transformStyle: "preserve-3d",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <SpecularShine progress={shineProgress} angle={28} coreWidth={90} haloWidth={260} borderRadius={38} />

                {/* Inner Real MLBB Gameplay Frame */}
                <div
                  style={{
                    position: "relative",
                    width: "100%",
                    height: 380,
                    borderRadius: 28,
                    overflow: "hidden",
                    boxShadow: "inset 0 2px 20px rgba(0, 0, 0, 0.4)",
                    transform: "translateZ(30px)",
                  }}
                >
                  <Img
                    src={staticFile("mlbb_ingame_gusion.jpg")}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      objectPosition: "center 30%",
                      filter: "contrast(1.05) saturate(1.1)",
                    }}
                  />

                  {/* Gameplay Tag */}
                  <div
                    style={{
                      position: "absolute",
                      top: 18,
                      left: 20,
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      backgroundColor: "rgba(18, 10, 46, 0.88)",
                      backdropFilter: "blur(8px)",
                      padding: "6px 16px",
                      borderRadius: 14,
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                    }}
                  >
                    <div style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#00F0FF", boxShadow: "0 0 8px #00F0FF" }} />
                    <span style={{ fontFamily: theme.fonts.technical, fontSize: 14, fontWeight: 700, color: "#FFFFFF", letterSpacing: "0.06em" }}>
                      AUTHENTIC GAMEPLAY SHOWCASE
                    </span>
                  </div>

                  {/* Anti-Minus Stamp */}
                  <div
                    style={{
                      position: "absolute",
                      bottom: 18,
                      right: 20,
                      backgroundColor: "rgba(16, 185, 129, 0.94)",
                      color: "#FFFFFF",
                      padding: "8px 18px",
                      borderRadius: 14,
                      fontFamily: theme.fonts.display,
                      fontSize: 24,
                      letterSpacing: "0.04em",
                      boxShadow: "0 8px 20px rgba(16, 185, 129, 0.4)",
                    }}
                  >
                    GARANSI ANTI-MINUS 100%
                  </div>
                </div>

                {/* Bottom Trust Pillars */}
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "16px 20px 0 20px",
                    transform: "translateZ(45px)",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={theme.colors.success} strokeWidth="3">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                    </svg>
                    <span style={{ fontFamily: theme.fonts.display, fontSize: 26, color: theme.colors.text }}>
                      CUKUP USER ID & ZONE ID
                    </span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={theme.colors.accent} strokeWidth="3">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <span style={{ fontFamily: theme.fonts.display, fontSize: 26, color: theme.colors.accent }}>
                      TANPA PASSWORD AKUN
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ===================================================================== */}
          {/* ACT 3: BREAK THE CENTER — SPLIT VIEWPORT & RADAR PROMO (462 - 715)    */}
          {/* 3D Weekly Pass Rp 25.000 (Left) vs Notification Radar (Right)         */}
          {/* ===================================================================== */}
          {frame >= 455 && frame < 720 && (
            <div
              style={{
                position: "absolute",
                transformStyle: "preserve-3d",
                opacity: interpolate(
                  frame,
                  [455, 470, 700, 718],
                  [0, 1, 1, 0],
                  { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
                ),
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                zIndex: 20,
              }}
            >
              <div
                style={{
                  backgroundColor: theme.colors.goldLight,
                  border: `2px solid ${theme.colors.gold}`,
                  padding: "6px 24px",
                  borderRadius: 22,
                  marginBottom: 14,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  boxShadow: "0 4px 15px rgba(255, 176, 32, 0.2)",
                }}
              >
                <div style={{ transform: `rotate(${bellRing}deg)` }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill={theme.colors.gold} stroke={theme.colors.gold} strokeWidth="1">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                    <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                  </svg>
                </div>
                <span style={{ fontFamily: theme.fonts.technical, fontSize: 18, fontWeight: 700, color: "#92400E", letterSpacing: "0.08em" }}>
                  FLASH SALE & RADAR NOTIFIKASI OTOMATIS
                </span>
              </div>

              <h1
                style={{
                  fontFamily: theme.fonts.display,
                  fontSize: 72,
                  textAlign: "center",
                  lineHeight: 1.0,
                  marginBottom: 20,
                }}
              >
                PROMO WEEKLY PASS <span style={{ color: theme.colors.accent }}>DAN RADAR SKIN MURAH</span>
              </h1>

              {/* Asymmetrical Split Stage */}
              <div
                style={{
                  position: "relative",
                  width: 860,
                  height: 520,
                  backgroundColor: theme.colors.surfaceSubtle,
                  backdropFilter: "blur(20px)",
                  borderRadius: 38,
                  border: `3px solid ${theme.colors.accent}`,
                  boxShadow: "0 35px 85px rgba(93, 63, 211, 0.22), 0 10px 25px rgba(0,0,0,0.05)",
                  padding: "32px 36px",
                  transformStyle: "preserve-3d",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 28,
                }}
              >
                <SpecularShine progress={shineProgress} angle={28} coreWidth={90} haloWidth={260} borderRadius={38} />

                {/* Left Card: 3D Weekly Pass Official Offer */}
                <div
                  style={{
                    flex: "0 0 340px",
                    height: "100%",
                    backgroundColor: theme.colors.surface,
                    borderRadius: 28,
                    border: "2px solid #E2E8F0",
                    padding: "24px 20px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    transform: "translateZ(55px)",
                    boxShadow: "0 15px 35px rgba(0,0,0,0.06)",
                    position: "relative",
                  }}
                >
                  <Img
                    src={staticFile("official_weekly_pass_clean.png")}
                    style={{
                      width: 200,
                      height: 200,
                      objectFit: "contain",
                      filter: "drop-shadow(0 15px 25px rgba(93, 63, 211, 0.3))",
                      marginBottom: 12,
                    }}
                  />
                  <div style={{ fontFamily: theme.fonts.display, fontSize: 32, color: theme.colors.text }}>
                    WEEKLY DIAMOND PASS
                  </div>
                  <div style={{ fontFamily: theme.fonts.display, fontSize: 48, color: theme.colors.accent, lineHeight: 0.95, margin: "6px 0" }}>
                    RP 25.000
                  </div>
                  <div style={{ fontFamily: theme.fonts.technical, fontSize: 14, fontWeight: 700, color: theme.colors.success }}>
                    HEMAT 70% • KUOTA FLASH SALE
                  </div>
                </div>

                {/* Right Card: Realtime Notification Radar */}
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 16,
                    transform: "translateZ(35px)",
                  }}
                >
                  <div
                    style={{
                      backgroundColor: theme.colors.surface,
                      borderRadius: 20,
                      border: `2px solid ${theme.colors.gold}`,
                      padding: "16px 20px",
                      display: "flex",
                      alignItems: "center",
                      gap: 14,
                      boxShadow: "0 6px 20px rgba(255, 176, 32, 0.15)",
                    }}
                  >
                    <div style={{ width: 44, height: 44, borderRadius: 12, backgroundColor: theme.colors.goldLight, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill={theme.colors.gold} stroke={theme.colors.gold} strokeWidth="1">
                        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                        <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                      </svg>
                    </div>
                    <div>
                      <div style={{ fontFamily: theme.fonts.technical, fontSize: 13, fontWeight: 700, color: "#92400E" }}>
                        ALERT FLASH SALE PRIORITAS
                      </div>
                      <div style={{ fontFamily: theme.fonts.display, fontSize: 26, color: theme.colors.text }}>
                        NOTIF LANGSUNG DIKIRIM KE HP
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      backgroundColor: theme.colors.surface,
                      borderRadius: 20,
                      border: "2px solid #E2E8F0",
                      padding: "16px 20px",
                      display: "flex",
                      alignItems: "center",
                      gap: 14,
                      boxShadow: "0 6px 20px rgba(0,0,0,0.04)",
                    }}
                  >
                    <div style={{ width: 44, height: 44, borderRadius: 12, backgroundColor: theme.colors.successLight, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={theme.colors.success} strokeWidth="3">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>
                      <div style={{ fontFamily: theme.fonts.technical, fontSize: 13, fontWeight: 700, color: theme.colors.success }}>
                        ANTI-KETINGGALAN EVENT
                      </div>
                      <div style={{ fontFamily: theme.fonts.display, fontSize: 26, color: theme.colors.text }}>
                        BORONG SEBELUM KUOTA LUDES
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      backgroundColor: theme.colors.darkBadge,
                      color: "#FFFFFF",
                      borderRadius: 20,
                      padding: "16px 20px",
                      display: "flex",
                      alignItems: "center",
                      gap: 14,
                      boxShadow: "0 10px 25px rgba(30, 18, 74, 0.3)",
                    }}
                  >
                    <div style={{ width: 44, height: 44, borderRadius: 12, backgroundColor: "rgba(16, 185, 129, 0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={theme.colors.success} strokeWidth="3">
                        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
                      </svg>
                    </div>
                    <div>
                      <div style={{ fontFamily: theme.fonts.technical, fontSize: 13, fontWeight: 700, color: "#00F0FF" }}>
                        SISTEM OTOMATIS
                      </div>
                      <div style={{ fontFamily: theme.fonts.display, fontSize: 26, color: "#FFFFFF" }}>
                        TANPA PERLU REFRESH MANUAL
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ===================================================================== */}
          {/* ACT 4: REALTIME ORDER TRACKING PROGRESS (715 - 845)                   */}
          {/* High-Contrast Stacked Pipeline: Step 1 ➔ Step 2 ➔ +220 Diamond        */}
          {/* ===================================================================== */}
          {frame >= 710 && frame < 850 && (
            <div
              style={{
                position: "absolute",
                transformStyle: "preserve-3d",
                opacity: interpolate(
                  frame,
                  [710, 725, 835, 848],
                  [0, 1, 1, 0],
                  { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
                ),
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                zIndex: 20,
              }}
            >
              <div
                style={{
                  backgroundColor: theme.colors.accentLight,
                  border: `2px solid ${theme.colors.accent}`,
                  padding: "6px 24px",
                  borderRadius: 22,
                  marginBottom: 14,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  boxShadow: "0 4px 15px rgba(93, 63, 211, 0.15)",
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={theme.colors.accent} strokeWidth="2.5">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                </svg>
                <span style={{ fontFamily: theme.fonts.technical, fontSize: 18, fontWeight: 700, color: theme.colors.accent, letterSpacing: "0.08em" }}>
                  TRANSPARANSI STATUS PESANAN LIVE
                </span>
              </div>

              <h1
                style={{
                  fontFamily: theme.fonts.display,
                  fontSize: 72,
                  textAlign: "center",
                  lineHeight: 1.0,
                  marginBottom: 20,
                }}
              >
                SELESAI SCAN QRIS, <span style={{ color: theme.colors.success }}>DIPANTAU DETIK PER DETIK</span>
              </h1>

              {/* 3D Master Glass Stage */}
              <div
                style={{
                  position: "relative",
                  width: 840,
                  height: 520,
                  backgroundColor: theme.colors.surfaceSubtle,
                  backdropFilter: "blur(20px)",
                  borderRadius: 38,
                  border: `3px solid ${theme.colors.accent}`,
                  boxShadow: "0 35px 85px rgba(93, 63, 211, 0.22), 0 10px 25px rgba(0,0,0,0.05)",
                  padding: "32px 36px",
                  transformStyle: "preserve-3d",
                  display: "flex",
                  flexDirection: "column",
                  gap: 18,
                }}
              >
                <SpecularShine progress={shineProgress} angle={28} coreWidth={90} haloWidth={260} borderRadius={38} />

                {/* Step 1: QRIS Payment Verified */}
                <div
                  style={{
                    backgroundColor: theme.colors.surface,
                    borderRadius: 24,
                    border: "2px solid #E2E8F0",
                    padding: "20px 28px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    transform: "translateZ(30px)",
                    boxShadow: "0 10px 25px rgba(0,0,0,0.04)",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
                    <div style={{ width: 52, height: 52, borderRadius: 16, backgroundColor: theme.colors.successLight, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={theme.colors.success} strokeWidth="3.5">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>
                      <div style={{ fontFamily: theme.fonts.technical, fontSize: 18, fontWeight: 700, color: theme.colors.muted }}>
                        LANGKAH 1
                      </div>
                      <div style={{ fontFamily: theme.fonts.display, fontSize: 34, color: theme.colors.text }}>
                        PEMBAYARAN QRIS TERVERIFIKASI
                      </div>
                    </div>
                  </div>
                  <div style={{ fontFamily: theme.fonts.technical, fontSize: 18, fontWeight: 700, color: theme.colors.success }}>
                    TERKONFIRMASI INSTAN
                  </div>
                </div>

                {/* Step 2: Direct Moonton Inject */}
                <div
                  style={{
                    backgroundColor: theme.colors.surface,
                    borderRadius: 24,
                    border: `2.5px solid ${theme.colors.success}`,
                    padding: "20px 28px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    transform: "translateZ(45px)",
                    boxShadow: "0 15px 35px rgba(16, 185, 129, 0.2)",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
                    <div style={{ width: 52, height: 52, borderRadius: 16, backgroundColor: theme.colors.successLight, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={theme.colors.success} strokeWidth="3.5">
                        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
                      </svg>
                    </div>
                    <div>
                      <div style={{ fontFamily: theme.fonts.technical, fontSize: 18, fontWeight: 700, color: theme.colors.success }}>
                        LANGKAH 2 (OFFICIAL MOONTON API)
                      </div>
                      <div style={{ fontFamily: theme.fonts.display, fontSize: 34, color: theme.colors.text }}>
                        DIRECT INJECT MOONTON SUKSES
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      backgroundColor: theme.colors.success,
                      color: "#FFFFFF",
                      padding: "8px 20px",
                      borderRadius: 14,
                      fontFamily: theme.fonts.technical,
                      fontSize: 18,
                      fontWeight: 800,
                    }}
                  >
                    0.82 DETIK
                  </div>
                </div>

                {/* Final Result Card */}
                <div
                  style={{
                    backgroundColor: theme.colors.darkBadge,
                    color: "#FFFFFF",
                    borderRadius: 26,
                    padding: "22px 32px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    transform: "translateZ(65px)",
                    boxShadow: "0 22px 50px rgba(30, 18, 74, 0.4)",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
                    <Img src={staticFile("diamond_3d_crystal.png")} style={{ width: 56, height: 56, objectFit: "contain" }} />
                    <div>
                      <div style={{ fontFamily: theme.fonts.technical, fontSize: 16, fontWeight: 700, color: "#00F0FF" }}>
                        HASIL INJECT SERVER REALTIME
                      </div>
                      <div style={{ fontFamily: theme.fonts.display, fontSize: 38 }}>
                        +220 DIAMOND SIAP DIGUNAKAN DI GAME!
                      </div>
                    </div>
                  </div>
                  <div style={{ fontFamily: theme.fonts.technical, fontSize: 18, fontWeight: 800, color: "#10B981" }}>
                    100% ANTI-MINUS
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ===================================================================== */}
          {/* ACT 5: GRAND CLIMAX & FINAL RESOLUTION CTA (845 - 1010)               */}
          {/* Anticipation Dip + 45-frame Intentional Hold                          */}
          {/* ===================================================================== */}
          {frame >= 840 && (
            <div
              style={{
                position: "absolute",
                transformStyle: "preserve-3d",
                opacity: interpolate(frame, [840, 855], [0, 1], { extrapolateLeft: "clamp" }),
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                zIndex: 20,
              }}
            >
              <div
                style={{
                  backgroundColor: theme.colors.successLight,
                  border: `2px solid ${theme.colors.success}`,
                  padding: "6px 24px",
                  borderRadius: 22,
                  marginBottom: 14,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  boxShadow: "0 4px 15px rgba(16, 185, 129, 0.15)",
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={theme.colors.success} strokeWidth="3">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                <span style={{ fontFamily: theme.fonts.technical, fontSize: 18, fontWeight: 700, color: theme.colors.success, letterSpacing: "0.08em" }}>
                  TOP UP TENANG • PROSES 0.82 DETIK SATSET
                </span>
              </div>

              <h1
                style={{
                  fontFamily: theme.fonts.display,
                  fontSize: 74,
                  textAlign: "center",
                  lineHeight: 1.0,
                  marginBottom: 24,
                }}
              >
                BUKA SEKARANG <span style={{ color: theme.colors.accent }}>DI KYUSTORE.COM</span>
              </h1>

              {/* Master CTA Card with Anticipation Dip */}
              <div
                style={{
                  position: "relative",
                  width: 840,
                  height: 520,
                  backgroundColor: theme.colors.surfaceSubtle,
                  backdropFilter: "blur(20px)",
                  borderRadius: 38,
                  border: `3px solid ${theme.colors.accent}`,
                  boxShadow: "0 35px 85px rgba(93, 63, 211, 0.22), 0 10px 25px rgba(0,0,0,0.05)",
                  padding: "40px",
                  transformStyle: "preserve-3d",
                  transform: `scale(${interpolate(ctaSpring, [0, 1], [0.85, 1])}) translateY(${ctaDip}px)`,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <SpecularShine progress={shineProgress} angle={28} coreWidth={90} haloWidth={260} borderRadius={38} />

                {/* Big Kyu Store Brand Unit */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 20,
                    marginBottom: 26,
                    transform: "translateZ(35px)",
                  }}
                >
                  <Img
                    src={staticFile("kyu_logo.png")}
                    style={{
                      height: 110,
                      objectFit: "contain",
                      filter: "drop-shadow(0 12px 28px rgba(93, 63, 211, 0.28))",
                    }}
                  />
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <span style={{ fontFamily: theme.fonts.display, fontSize: 56, color: theme.colors.text, lineHeight: 0.95 }}>
                      KYU STORE
                    </span>
                    <span style={{ fontFamily: theme.fonts.technical, fontSize: 18, fontWeight: 700, color: theme.colors.success, letterSpacing: "0.1em" }}>
                      DISTRIBUTOR RESMI MOONTON INDONESIA
                    </span>
                  </div>
                </div>

                {/* Big Glowing CTA Button with Pulse */}
                <div
                  style={{
                    backgroundColor: theme.colors.success,
                    color: "#FFFFFF",
                    padding: "22px 54px",
                    borderRadius: 26,
                    display: "flex",
                    alignItems: "center",
                    gap: 16,
                    boxShadow: "0 18px 45px rgba(16, 185, 129, 0.38)",
                    transform: `translateZ(50px) scale(${ctaPulse})`,
                    marginBottom: 26,
                  }}
                >
                  <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="3.2">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  <span style={{ fontFamily: theme.fonts.display, fontSize: 42, letterSpacing: "0.04em" }}>
                    TOP UP SEKARANG: KYUSTORE.COM
                  </span>
                </div>

                {/* Trust Points */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 32,
                    fontFamily: theme.fonts.headline,
                    fontSize: 20,
                    fontWeight: 800,
                    color: theme.colors.muted,
                    transform: "translateZ(30px)",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={theme.colors.success} strokeWidth="3">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <span>100% Legal & Aman</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={theme.colors.success} strokeWidth="3">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <span>0.82 Detik Satset</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={theme.colors.success} strokeWidth="3">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <span>Cukup User ID & Zone</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 18 Atmospheric Floating Light Motes */}
          <AtmosphericParticles frame={frame} count={18} />
        </div>
      </div>

      {/* ========================================================================= */}
      {/* FOOTER TICKER (Safe Zone Bottom 80px)                                     */}
      {/* ========================================================================= */}
      <div
        style={{
          position: "absolute",
          bottom: 80,
          left: theme.spacing.page,
          right: theme.spacing.page,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: theme.colors.surface,
          border: `2px solid ${theme.colors.border}`,
          borderRadius: 22,
          padding: "14px 30px",
          boxShadow: "0 6px 20px rgba(0,0,0,0.04)",
          zIndex: 50,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, fontFamily: theme.fonts.headline, fontSize: 18, fontWeight: 800, color: theme.colors.muted }}>
          <span style={{ color: theme.colors.accent }}>METODE RESMI:</span>
          <span>QRIS • BCA • MANDIRI • BRI • DANA • GOPAY • SHOPEEPAY</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: theme.fonts.technical, fontSize: 16, fontWeight: 700, color: theme.colors.success }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={theme.colors.success} strokeWidth="3">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
          <span>AUTO INJECT ACTIVE 24/7</span>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* DYNAMIC SUBTITLES: PINNED AT OPTIMAL SAFE BAND (bottom: 400px)            */}
      {/* ========================================================================= */}
      <DynamicSubtitle frame={frame} subtitles={SUBTITLES} bottom={400} />
    </AbsoluteFill>
  );
};
